import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const CAMPAIGN_TYPES = [
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'whatsapp_image', label: 'WhatsApp Image' },
  { value: 'whatsapp_video', label: 'WhatsApp Video' },
  { value: 'whatsapp_document', label: 'WhatsApp Document' },
  { value: 'rcs', label: 'RCS Message' },
  { value: 'status_messaging', label: 'Status Messaging' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'push_notification', label: 'Push Notification' },
  { value: 'voice_call', label: 'Voice Call' },
  { value: 'custom', label: 'Custom' },
];

export default function CampaignManagement() {
  const [campaigns, setCampaigns] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'sms', failCount: 5, dataLimit: 0, allowReuse: false });
  const [loading, setLoading] = useState(true);
  const [availableData, setAvailableData] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const navigate = useNavigate();

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/campaigns?page=${page}`);
      setCampaigns(data.campaigns);
      setPages(data.pages);
      setTotal(data.total);
    } catch { toast.error('Failed to load campaigns'); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const openCreate = () => {
    setEditing(null); setForm({ name: '', type: 'sms', failCount: 5, dataLimit: 0, allowReuse: false });
    api.get('/admin/campaigns/available-data').then(({ data }) => { setAvailableData(data.availableData || 0); setTotalData(data.totalData || 0); }).catch(() => {});
    setShowModal(true);
  };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, type: c.type, failCount: c.failCount, dataLimit: c.dataLimit || 0, allowReuse: c.allowReuse || false }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/campaigns/${editing._id}`, form);
        toast.success('Campaign updated');
      } else {
        await api.post('/admin/campaigns', form);
        toast.success('Campaign created');
      }
      setShowModal(false);
      fetchCampaigns();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign and all associated data?')) return;
    try { await api.delete(`/admin/campaigns/${id}`); toast.success('Deleted'); fetchCampaigns(); }
    catch { toast.error('Delete failed'); }
  };

  const handleStatusChange = async (id, status) => {
    try { await api.patch(`/admin/campaigns/${id}/status`, { status }); toast.success('Status updated'); fetchCampaigns(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Campaigns</h1>
          <p className="page-subtitle">{total} campaigns</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Create Campaign</button>
      </div>

      <div className="card">
        {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th><th>Type</th><th>Status</th><th>Fail Count</th><th>Data Limit</th><th>Reuse</th><th>Assigned</th><th>Sent</th><th>Failed</th><th>Creator</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(c => (
                  <tr key={c._id}>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/campaigns/${c._id}`)}>
                      {c.name}
                    </td>
                    <td><span className={`badge badge-${c.type}`}>{(c.type || '').replace('_', ' ')}</span></td>
                    <td>
                      <select value={c.status} onChange={e => handleStatusChange(c._id, e.target.value)}
                        className="form-select" style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}>
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                        <option value="stopped">Stopped</option>
                        <option value="completed">Completed</option>
                      </select>
                    </td>
                    <td>{c.failCount || 0}</td>
                    <td style={{ color: '#7ea52e', fontWeight: 600 }}>{(c.dataLimit || 0).toLocaleString()}</td>
                    <td>{c.allowReuse ? <span className="badge" style={{ background: '#f0fdf4', color: '#16a34a', fontSize: 10 }}>Yes</span> : <span style={{ color: '#ccc' }}>—</span>}</td>
                    <td style={{ fontWeight: 600 }}>{(c.totalAssigned || 0).toLocaleString()}</td>
                    <td style={{ color: '#22c55e', fontWeight: 600 }}>{(c.totalSent || 0).toLocaleString()}</td>
                    <td style={{ color: '#ef4444', fontWeight: 600 }}>{(c.totalFailed || 0).toLocaleString()}</td>
                    <td>{c.createdBy?.username}</td>
                    <td>
                      <div className="actions">
                        <button className="action-btn view" onClick={() => navigate(`/admin/campaigns/${c._id}`)}>View</button>
                        <button className="action-btn edit" onClick={() => openEdit(c)}>Edit</button>
                        <button className="action-btn delete" onClick={() => handleDelete(c._id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {campaigns.length === 0 && <tr><td colSpan="11" className="empty-state">No campaigns yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="pagination">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            {[...Array(pages)].map((_, i) => (
              <button key={i} className={page === i + 1 ? 'active' : ''} onClick={() => setPage(i + 1)}>{i + 1}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>›</button>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Campaign' : 'Create Campaign'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Campaign Name</label>
                  <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select className="form-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                      {CAMPAIGN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fail Count (how many records to fail)</label>
                    <input type="number" className="form-input" min="0" step="1"
                      value={form.failCount} onChange={e => setForm({ ...form, failCount: parseInt(e.target.value) || 0 })} />
                  </div>
                </div>
                {!editing && (
                  <div className="form-group">
                    <label className="form-label">Data Records from Data Hub</label>
                    <input type="number" className="form-input" min="0" max={form.allowReuse ? totalData : availableData}
                      value={form.dataLimit} onChange={e => setForm({ ...form, dataLimit: parseInt(e.target.value) || 0 })} 
                      placeholder="0 = no auto-assign" />
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                      {availableData.toLocaleString()} unassigned{form.allowReuse ? ` • ${totalData.toLocaleString()} total (reuse on)` : ''} — 0 = skip auto-assign
                    </span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, cursor: 'pointer', fontSize: 13 }}>
                      <input type="checkbox" checked={form.allowReuse}
                        onChange={e => setForm({ ...form, allowReuse: e.target.checked })}
                        style={{ width: 16, height: 16, accentColor: '#7ea52e' }} />
                      <span>Allow reuse of already-assigned data</span>
                    </label>
                    {form.allowReuse && (
                      <span style={{ fontSize: 11, color: '#f97316', marginTop: 4, display: 'block' }}>
                        ⚠ If unassigned data isn't enough, records already assigned to other campaigns will be reused.
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
